/**
 * Router seam (RO-4-01) — the four things the ~110 `next/navigation` /
 * `next/link` importers in this package actually use, behind one React
 * context. `packages/ui` stays router-agnostic (D2: target is TanStack
 * Router, but this package must not know that either).
 *
 * Hosts MUST supply a `RouterAdapter` via `RouterProvider` (the Vite shell
 * does this in `client/shell/router-adapter.tsx`). The default adapter throws
 * on use — there is no Next host left after RO-10-09 deleted `excellent/app/`,
 * so a missing provider is a wiring bug, not a supported fallback.
 */

import * as React from "react";

export type NavigateOptions = { scroll?: boolean };

export type RouterLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string;
};

export interface RouterAdapter {
  Link: React.ComponentType<RouterLinkProps>;
  useNavigate: () => {
    push: (href: string, options?: NavigateOptions) => void;
    replace: (href: string, options?: NavigateOptions) => void;
    back: () => void;
    refresh: () => void;
  };
  usePathname: () => string;
  useSearch: () => URLSearchParams;
}

function missingProvider(what: string): never {
  throw new Error(
    `[router] ${what} requires <RouterProvider adapter={…}> — the Vite shell wires tanstackAdapter in client/shell/router-adapter.tsx`,
  );
}

/** Throws on use so a missing RouterProvider fails loudly (not a Next fallback). */
const unsetAdapter: RouterAdapter = {
  Link: () => missingProvider("RouterLink"),
  useNavigate: () => missingProvider("useNavigate"),
  usePathname: () => missingProvider("usePathname"),
  useSearch: () => missingProvider("useSearch"),
};

const RouterContext = React.createContext<RouterAdapter>(unsetAdapter);

/** A host app supplies its own `RouterAdapter` here; required at runtime. */
export function RouterProvider({
  adapter,
  children,
}: {
  adapter: RouterAdapter;
  children: React.ReactNode;
}) {
  return <RouterContext.Provider value={adapter}>{children}</RouterContext.Provider>;
}

// The four seam exports — call sites use these instead of `next/*` directly.
export const RouterLink: React.ComponentType<RouterLinkProps> = (props) => {
  const { Link: AdapterLink } = React.useContext(RouterContext);
  return <AdapterLink {...props} />;
};

export function useNavigate() {
  return React.useContext(RouterContext).useNavigate();
}

export function usePathname() {
  return React.useContext(RouterContext).usePathname();
}

export function useSearch() {
  return React.useContext(RouterContext).useSearch();
}
